import React, { useState } from "react";
import { storePlaybookLead } from "../../util/http";
import { trackEvent } from "../../lib/analytics";

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function normalizeFormLocation(source) {
  return String(source || "playbook_form")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 64);
}

const SUCCESS_COPY =
  "Check your inbox — we just sent the Menu Training Playbook PDF to {email} as an email attachment. If you don’t see it in 2 minutes, check spam.";

function buildDuplicateCopy(emailStatus) {
  if (emailStatus === "sent") {
    return "This email is already in our system. We already sent the Menu Training Playbook as an email attachment. Please check your inbox or spam folder.";
  }

  if (emailStatus === "sending" || emailStatus === "queued") {
    return "This email is already in our system. Your previous request is still being processed. Please check your inbox shortly.";
  }

  return "This email is already in our system. We already received your request. If you still need the Menu Training Playbook email, contact us and we will help.";
}

export default function PlaybookCaptureForm({
  source,
  compact = false,
  className = "",
  buttonLabel = "Send me the playbook.",
  inputPlaceholder = "Work email",
  helpText = "We’ll email the PDF as an attachment.",
}) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();

    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    setSubmissionMessage("");

    try {
      const response = await storePlaybookLead({
        email: normalizedEmail,
        source,
        landingPath: typeof window !== "undefined" ? window.location.pathname + window.location.search + window.location.hash : "",
        referrer: typeof document !== "undefined" ? document.referrer || "" : "",
        website,
      });

      if (response?.duplicate) {
        setSubmissionMessage(buildDuplicateCopy(response.emailStatus));
      } else {
        trackEvent("generate_lead", {
          lead_type: "playbook",
          form_location: normalizeFormLocation(source),
          page_path: typeof window !== "undefined" ? window.location.pathname : "",
        });
        setSubmissionMessage(SUCCESS_COPY.replace("{email}", normalizedEmail));
      }

      setEmail("");
      setWebsite("");
    } catch (submitError) {
      setError("We couldn’t send the PDF right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submissionMessage) {
    return (
      <div className={`homepage-playbook-success ${className}`.trim()} role="status" aria-live="polite">
        {submissionMessage}
      </div>
    );
  }

  return (
    <form className={`homepage-playbook-form ${compact ? "homepage-playbook-form-compact" : ""} ${className}`.trim()} onSubmit={handleSubmit} noValidate>
      <div className="homepage-playbook-fields">
        <label className="visually-hidden" htmlFor={`playbook-email-${source}`}>
          Email address
        </label>
        <label className="visually-hidden" htmlFor={`playbook-website-${source}`}>
          Website
        </label>
        <input
          id={`playbook-website-${source}`}
          type="text"
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          className="visually-hidden"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <input
          id={`playbook-email-${source}`}
          type="email"
          name="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) setError("");
          }}
          className={`homepage-playbook-input${error ? " homepage-playbook-input-error" : ""}`}
          placeholder={inputPlaceholder}
          autoComplete="email"
          inputMode="email"
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `playbook-email-error-${source}` : `playbook-email-help-${source}`}
        />
        <button type="submit" className="homepage-playbook-button" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : buttonLabel}
        </button>
      </div>
      {error ? (
        <div id={`playbook-email-error-${source}`} className="homepage-playbook-error">
          {error}
        </div>
      ) : null}
      <p id={`playbook-email-help-${source}`} className="homepage-playbook-help">
        {helpText}
      </p>
    </form>
  );
}